import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Globe,
  Palette,
  Layout,
  Type,
  Image,
  Settings,
  Sparkles,
  ShoppingBag,
  User
} from 'lucide-react';
import toast from 'react-hot-toast';

const steps = [
  { id: 1, name: 'Template', icon: Layout },
  { id: 2, name: 'Name', icon: Type },
  { id: 3, name: 'Style', icon: Palette },
  { id: 4, name: 'Pages', icon: Globe },
  { id: 5, name: 'Content', icon: Sparkles },
  { id: 6, name: 'E-commerce', icon: ShoppingBag },
  { id: 7, name: 'Domain', icon: Globe },
  { id: 8, name: 'Review', icon: Check },
];

const templates = [
  { id: 'business', name: 'Business', description: 'Professional corporate site', color: 'from-blue-500 to-blue-600' },
  { id: 'portfolio', name: 'Portfolio', description: 'Showcase your work', color: 'from-purple-500 to-purple-600' },
  { id: 'store', name: 'Online Store', description: 'Sell products online', color: 'from-green-500 to-green-600' },
  { id: 'blog', name: 'Blog', description: 'Share your stories', color: 'from-orange-500 to-orange-600' },
  { id: 'restaurant', name: 'Restaurant', description: 'Food & dining', color: 'from-red-500 to-red-600' },
  { id: 'event', name: 'Event', description: 'Promote your event', color: 'from-pink-500 to-pink-600' },
];

const colorSchemes = [
  { id: 'blue', name: 'Ocean', primary: '#3B82F6', secondary: '#1E40AF' },
  { id: 'purple', name: 'Royal', primary: '#8B5CF6', secondary: '#5B21B6' },
  { id: 'green', name: 'Forest', primary: '#10B981', secondary: '#047857' },
  { id: 'orange', name: 'Sunset', primary: '#F59E0B', secondary: '#B45309' },
  { id: 'red', name: 'Cherry', primary: '#EF4444', secondary: '#B91C1C' },
  { id: 'gray', name: 'Sleek', primary: '#6B7280', secondary: '#1F2937' },
];

const WebsiteWizard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [websiteData, setWebsiteData] = useState({
    template: 'business',
    name: '',
    description: '',
    colorScheme: 'blue',
    pages: ['home', 'about', 'contact'],
    content: {
      headline: '',
      subheadline: '',
      about: ''
    },
    ecommerce: {
      enabled: false,
      currency: 'USD'
    },
    domain: ''
  });

  const handleNext = () => {
    if (currentStep === 2 && !websiteData.name) {
      toast.error('Please enter a website name');
      return;
    }
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      
      const website = {
        userId: user.uid,
        name: websiteData.name,
        description: websiteData.description,
        template: websiteData.template,
        colorScheme: websiteData.colorScheme,
        pages: websiteData.pages,
        content: websiteData.content,
        ecommerce: websiteData.ecommerce,
        domain: websiteData.domain || null,
        status: 'draft',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'websites'), website);
      
      toast.success('Website created successfully!');
      navigate(`/site/editor/${docRef.id}`);
    } catch (error) {
      console.error('Error creating website:', error);
      toast.error('Failed to create website');
      setCreating(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Choose a Template</h2>
            <p className="text-gray-500">Select a template that best fits your needs</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setWebsiteData({...websiteData, template: template.id})}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${
                    websiteData.template === template.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${template.color} rounded-xl mb-4`} />
                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-500">{template.description}</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Name Your Website</h2>
            <p className="text-gray-500">Give your website a memorable name</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website Name *</label>
                <input
                  type="text"
                  value={websiteData.name}
                  onChange={(e) => setWebsiteData({...websiteData, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="My Awesome Website"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={websiteData.description}
                  onChange={(e) => setWebsiteData({...websiteData, description: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  placeholder="Brief description of your website..."
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Choose Your Style</h2>
            <p className="text-gray-500">Pick a color scheme for your website</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {colorSchemes.map((scheme) => (
                <button
                  key={scheme.id}
                  onClick={() => setWebsiteData({...websiteData, colorScheme: scheme.id})}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    websiteData.colorScheme === scheme.id
                      ? 'border-primary-500'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex gap-2 mb-3">
                    <div
                      className="w-8 h-8 rounded-full"
                      style={{ backgroundColor: scheme.primary }}
                    />
                    <div
                      className="w-8 h-8 rounded-full"
                      style={{ backgroundColor: scheme.secondary }}
                    />
                  </div>
                  <p className="font-medium text-gray-900">{scheme.name}</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Select Pages</h2>
            <p className="text-gray-500">Choose which pages to include</p>
            <div className="space-y-3">
              {['home', 'about', 'services', 'portfolio', 'blog', 'contact', 'faq'].map((page) => (
                <label
                  key={page}
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100"
                >
                  <input
                    type="checkbox"
                    checked={websiteData.pages.includes(page)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setWebsiteData({...websiteData, pages: [...websiteData.pages, page]});
                      } else {
                        setWebsiteData({...websiteData, pages: websiteData.pages.filter(p => p !== page)});
                      }
                    }}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <span className="font-medium text-gray-900 capitalize">{page}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Add Your Content</h2>
            <p className="text-gray-500">Customize your homepage content</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Headline</label>
                <input
                  type="text"
                  value={websiteData.content.headline}
                  onChange={(e) => setWebsiteData({
                    ...websiteData,
                    content: {...websiteData.content, headline: e.target.value}
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Welcome to our website"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subheadline</label>
                <input
                  type="text"
                  value={websiteData.content.subheadline}
                  onChange={(e) => setWebsiteData({
                    ...websiteData,
                    content: {...websiteData.content, subheadline: e.target.value}
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="We provide the best services"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">About</label>
                <textarea
                  value={websiteData.content.about}
                  onChange={(e) => setWebsiteData({
                    ...websiteData,
                    content: {...websiteData.content, about: e.target.value}
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={4}
                  placeholder="Tell visitors about your business..."
                />
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">E-commerce Setup</h2>
            <p className="text-gray-500">Configure your online store</p>
            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={websiteData.ecommerce.enabled}
                onChange={(e) => setWebsiteData({
                  ...websiteData,
                  ecommerce: {...websiteData.ecommerce, enabled: e.target.checked}
                })}
                className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="font-medium text-gray-900">Enable E-commerce</span>
            </label>
            {websiteData.ecommerce.enabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={websiteData.ecommerce.currency}
                  onChange={(e) => setWebsiteData({
                    ...websiteData,
                    ecommerce: {...websiteData.ecommerce, currency: e.target.value}
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="BDT">BDT - Bangladeshi Taka</option>
                  <option value="INR">INR - Indian Rupee</option>
                </select>
              </div>
            )}
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Connect Your Domain</h2>
            <p className="text-gray-500">Add a custom domain (optional)</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
              <input
                type="text"
                value={websiteData.domain}
                onChange={(e) => setWebsiteData({...websiteData, domain: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="www.yourdomain.com"
              />
              <p className="text-sm text-gray-500 mt-2">
                Leave empty to use a subdomain (yoursite.hostbit.cloud)
              </p>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Review Your Website</h2>
            <p className="text-gray-500">Double-check everything before creating</p>
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-500">Template</span>
                <span className="font-medium capitalize">{websiteData.template}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Name</span>
                <span className="font-medium">{websiteData.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Color Scheme</span>
                <span className="font-medium capitalize">{websiteData.colorScheme}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Pages</span>
                <span className="font-medium">{websiteData.pages.length} pages</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">E-commerce</span>
                <span className="font-medium">{websiteData.ecommerce.enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Domain</span>
                <span className="font-medium">{websiteData.domain || 'Subdomain'}</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentStep >= step.id
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className="text-xs mt-1 text-gray-500">{step.name}</span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      currentStep > step.id ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>
          
          {currentStep === steps.length ? (
            <button
              onClick={handleCreate}
              disabled={creating}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              {creating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Create Website
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebsiteWizard;
